import t from '../../helpers/Languages.js'
import OrderActions from '../../actions/OrderActions.js'
import time from '../../../server/utils/time.js'
import {getRouteBulkActionDisabledReason, RouteBulkAction} from '../../utils/routeBulkActions.js'
import type {PopupRef} from '../UI/overlays/Popup.js'
import type {FrontEndRoute} from '../../../server/functions/route/route.types.js'

type TranslationKey = Parameters<typeof t>[0]

/** Icon and label (a translation key) of the bulk actions, for the Dagplanning and the Routes table */
export const routeBulkActionOptions: Record<RouteBulkAction, {icon: string, label: TranslationKey}> = {
    hide: {icon: 'mdi mdi-map-marker-off', label: 'hideRoutes'},
    optimize: {icon: 'mdi mdi-map-marker-path', label: 'optimizeRoutes'},
    ready: {icon: 'mdi mdi-check', label: 'makeRoutesReady'},
    unready: {icon: 'mdi mdi-pencil', label: 'editRoutes'},
    reschedule: {icon: 'mdi mdi-calendar', label: 'moveRoutes'},
    stop: {icon: 'mdi mdi-flag-checkered', label: 'completeRoutes'},
    remove: {icon: 'mdi mdi-delete', label: 'deleteRoutes'}
}

const bulkRouteActionTimeout = 2 * 60 * 1000

type ActionResult = {err?: string | Error, errors?: string[]}

function runForRoute(action: RouteBulkAction, routeId: string, date?: string): Promise<ActionResult> {
    return new Promise((resolve) => {
        // A callback that never comes (e.g. after a dropped connection) shouldn't block the remaining routes
        const timeout = setTimeout(() => {
            // The store only clears the loading state when the callback comes, so the route would keep spinning
            OrderActions.setRouteLoading(routeId, false)
            resolve({err: t('noResponseFromServer')})
        }, bulkRouteActionTimeout)

        const callback = (err?: string | Error, errors?: string[]) => {
            clearTimeout(timeout)
            resolve({err, errors})
        }

        switch (action) {
            case 'hide':
                OrderActions.setRouteHidden(routeId, true)
                return callback()
            case 'optimize':
                return OrderActions.planRoute(routeId, true, false, undefined, callback)
            case 'ready':
                return OrderActions.setRouteReady(routeId, true, callback)
            case 'unready':
                return OrderActions.setRouteReady(routeId, false, callback)
            case 'reschedule':
                return OrderActions.rescheduleRoute(routeId, date, callback)
            case 'stop':
                return OrderActions.stopRoute(routeId, callback)
            case 'remove':
                return OrderActions.removeRoute(routeId, callback)
        }
    })
}

interface RunRouteBulkActionOptions {
    popup: PopupRef
    /** The route as it is now, it may have changed or been removed since it was selected */
    getRoute: (routeId: string) => FrontEndRoute | undefined
    /** The warnings shown before routes are made ready */
    getReadyWarnings?: (route: FrontEndRoute) => string[]
    /** Once confirmed, before the first route */
    onStart?: () => void
}

/**
 * Does a bulk action on the routes: asks to confirm (and for the date to move to), then does it route by route with a
 * progress bar and shows the routes it failed for. Routes the action can't be done on are skipped and shown with the
 * failed ones. Resolves false when nothing was done (cancelled, or no route the action can be done on).
 */
export default async function runRouteBulkAction(action: RouteBulkAction, routes: FrontEndRoute[], options: RunRouteBulkActionOptions): Promise<boolean> {
    const {popup, getRoute, getReadyWarnings, onStart} = options
    const errors: string[] = []

    const selectedRoutes = routes.filter((route) => {
        const disabledReason = getRouteBulkActionDisabledReason(action, route)

        if (disabledReason) {
            errors.push(`${route.name}: ${disabledReason}`)
        }

        return !disabledReason
    })

    if (!selectedRoutes.length) {
        return false
    }

    let date: string | undefined
    let title = ''
    let text = ''

    if (action === 'ready') {
        const warnings = selectedRoutes.reduce((warnings: string[], route) => {
            const routeWarnings = getReadyWarnings?.(route) || []

            if (routeWarnings.length) {
                warnings.push([route.name, ...routeWarnings].join('\n'))
            }

            return warnings
        }, [])

        popup.setWarnings(warnings)
        title = t('makeRoutesReady')
        text = t('routeBulkReadyText')
    }

    if (action === 'unready') {
        title = t('editRoutes')
        text = t('routeBulkUnreadyText')
    }

    if (action === 'reschedule') {
        popup.setInputs([{name: 'date', type: 'date', value: time.tomorrow(), label: t('date'), required: true}])
        title = t('moveRoutes')
        text = t('routeBulkRescheduleText')
    }

    if (action === 'stop') {
        title = t('completeRoutes')
        text = t('routeBulkStopText')
    }

    if (action === 'remove') {
        const lines = [t('routeBulkRemoveText')]

        if (selectedRoutes.some((route) => route.templateId)) {
            lines.push(t('routeBulkRemoveTemplateRoutes'))
        }

        if (selectedRoutes.some((route) => route.addresses?.length)) {
            lines.push(t('routeBulkRemoveRoutesWithStops'))
        }

        title = t('deleteRoutes')
        text = lines.join('\n')
    }

    if (title) {
        // Undefined when closed without confirming
        const value = await popup.open(title, text)

        if (!value) {
            return false
        }

        if (action === 'reschedule') {
            date = value.date
        }
    }

    let failed = errors.length

    const run = async (onProgress?: (now: number, max: number) => void) => {
        for (const [index, route] of selectedRoutes.entries()) {
            const nrOfErrors = errors.length

            // Check again, the route may have changed or been removed since it was selected (e.g. made ready by a colleague while the popup was open)
            const currentRoute = getRoute(route._id)
            const disabledReason = currentRoute ? getRouteBulkActionDisabledReason(action, currentRoute) : t('routeBulkRemoved')

            if (disabledReason) {
                errors.push(`${route.name}: ${disabledReason}`)
            } else {
                const {err, errors: routeErrors} = await runForRoute(action, route._id, date)

                if (err) {
                    errors.push(`${route.name}: ${typeof err === 'string' ? err : err.message}`)
                }

                routeErrors?.forEach((error) => errors.push(`${route.name}: ${error}`))
            }

            if (errors.length > nrOfErrors) {
                failed += 1
            }

            onProgress?.(index + 1, selectedRoutes.length)
        }
    }

    onStart?.()

    if (title) {
        await popup.openProgress(title, text, run)
    } else {
        await run()
    }

    if (errors.length) {
        const total = routes.length
        popup.open(t('routeBulkErrorsTitle'), t('routeBulkErrorsText', {success: total - failed, total}))
        popup.setErrors(errors)
    }

    return true
}
